import { Image as MantineImage, Skeleton } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";

interface OptimizedImageProps {
	src: string | undefined;
	thumbnail?: string;
	height?: number | string;
	width?: number;
	alt?: string;
	fallbackSrc?: string;
	style?: React.CSSProperties;
}

async function preloadImage(src: string): Promise<string> {
	return new Promise((resolve, reject) => {
		const img = new window.Image();
		img.src = src;

		const cacheBust = localStorage.getItem("cache-bust");
		// https://stackoverflow.com/a/729623
		if (cacheBust) {
			img.src = src;
		}
		img.onload = () => resolve(src);
		img.onerror = reject;
	});
}

export function OptimizedImage({
	src,
	height = "100%",
	width,
	alt = "",
	fallbackSrc = "https://placehold.co/200x200/e9e8ed/717089?text=No+Image",
	style,
}: OptimizedImageProps) {
	const {
		data: loadedSrc,
		isError: _srcError,
		isLoading: srcLoading,
	} = useQuery({
		queryKey: ["image", src],
		queryFn: async () => {
			return src ? preloadImage(src) : Promise.reject("No source");
		},
		enabled: !!src,
		staleTime: 1000 * 60 * 60 * 24, // Cache for 24 hour
		gcTime: 1000 * 60 * 60 * 24, // Keep in garbage collection for 24 hours
		retry: false,
	});

	const MIN_HEIGHT = 300;

	if (srcLoading) {
		return (
			<Skeleton
				height={height}
				width={width}
				style={{
					minHeight: MIN_HEIGHT,
				}}
			/>
		);
	}

	return (
		<MantineImage
			src={loadedSrc}
			fallbackSrc={fallbackSrc}
			height={height}
			width={width}
			alt={alt}
			loading="lazy"
			style={{
				objectFit: "cover",
				transition: "filter 0.2s ease-in-out",
				...style,
			}}
		/>
	);
}
